/* @flow weak */
import Button from 'components/Button.jsx';
import Glyph from 'components/Glyph.jsx';
import { getPropClassesFromInstance } from 'lib/getPropClasses';
import React from 'react';
import ResponsiveMixin from 'components/mixins/ResponsiveMixin';
import Slider from 'components/Slider.jsx';
import cx from 'classnames';
import formatMediaTime from 'utils/formatMediaTime';
import './styles/MediaPlayer.scss';

const MediaPlayer = React.createClass({
  propTypes: {
    playerId: React.PropTypes.string.isRequired,
    theme: React.PropTypes.oneOf(['light', 'dark']).isRequired,
    fullscreen: React.PropTypes.bool,
    downloadable: React.PropTypes.bool,
    src: React.PropTypes.string,
  },

  mixins: [
    ResponsiveMixin,
  ],

  statics: {
    propClasses: {
      theme: {
        light: 'MediaPlayer-light',
        dark: 'MediaPlayer-dark',
      },
      fullscreen: 'MediaPlayer-fullscreenEnabled',
    },
    SMALL: 440,
  },

  getDefaultProps() {
    return {
      fullscreen: false,
      downloadable: true,
    };
  },

  getInitialState() {
    return {
      playing: false,
      wasPlaying: false,
      currentTime: 0,
      totalTime: 0,
      muted: false,
      volume: 1.0,
    };
  },

  componentDidMount() {
    const player = this.getPlayer();

    if (player.tagName === 'VIDEO') {
      player.addEventListener('click', this.togglePlayback, false);
    }
    player.addEventListener('durationchange', this.onDurationChange, false);
    player.addEventListener('timeupdate', this.onTimeUpdate, false);
    player.addEventListener('volumechange', this.onVolumeChange, false);
    player.addEventListener('play', this.onPlay, false);
    player.addEventListener('pause', this.onPause, false);
    player.addEventListener('ended', this.onEnded, false);
  },

  componentWillUnmount() {
    const player = this.getPlayer();

    if (player.tagName === 'VIDEO') {
      player.removeEventListener('click', this.togglePlayback, false);
    }
    player.removeEventListener('durationchange', this.onDurationChange, false);
    player.removeEventListener('timeupdate', this.onTimeUpdate, false);
    player.removeEventListener('volumechange', this.onVolumeChange, false);
    player.removeEventListener('play', this.onPlay, false);
    player.removeEventListener('pause', this.onPause, false);
    player.removeEventListener('ended', this.onEnded, false);
  },

  onAfterChangePlayback() {
    if (this.state.wasPlaying) {
      this.play();
    }
  },

  onBeforeChangePlayback() {
    this.setState(({ playing }) => ({ wasPlaying: playing, playing: false }));
    this.pause();
  },

  onChangePlayback(value) {
    this.getPlayer().currentTime = value;
  },

  onChangeVolume(value) {
    this.getPlayer().volume = value;
  },

  onDurationChange() {
    this.setState({ totalTime: this.getPlayer().duration });
  },

  onEnded() {
    this.setState({ playing: false });
  },

  onPause() {
    if (this.state.playing) {
      this.setState({ playing: false });
    }
  },

  onPlay() {
    this.setState({ playing: true });
  },

  onTimeUpdate() {
    this.setState({ currentTime: this.getPlayer().currentTime });
  },

  onVolumeChange(event) {
    if (this.state.muted !== event.target.muted) { // Mute was changed
      this.setState({ muted: event.target.muted });
    } else { // Volume was changed
      if (event.target.volume === 0.0) {
        this.mute();
      } else {
        this.unmute();
      }

      this.setState({ volume: event.target.volume });
    }
  },

  getButtonColor() {
    return (this.props.theme === 'dark') ? 'white' : 'brand';
  },

  getPlayer() {
    return document.getElementById(this.props.playerId);
  },

  makeFullscreen() {
    const player = this.getPlayer();

    if (player.requestFullscreen) {
      player.requestFullscreen();
    } else if (player.msRequestFullscreen) {
      player.msRequestFullscreen();
    } else if (player.mozRequestFullScreen) {
      player.mozRequestFullScreen();
    } else if (player.webkitRequestFullscreen) {
      player.webkitRequestFullscreen();
    }
  },

  mute() {
    this.getPlayer().muted = true;
  },

  pause() {
    this.getPlayer().pause();
  },

  play() {
    this.getPlayer().play();
  },

  toggleMute() {
    if (this.state.muted) {
      this.unmute();
    } else {
      this.mute();
    }
  },

  togglePlayback() {
    if (this.state.playing) {
      this.pause();
    } else {
      this.play();
    }
  },

  unmute() {
    this.getPlayer().muted = false;
  },

  renderDownload() {
    if (!this.props.downloadable) {
      return null;
    }

    return (
      <Button
        download
        href={this.props.src}
        className="MediaPlayer-download"
        level="icon"
        color={this.getButtonColor()}
        tooltip="Download"
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-download">
        <Glyph name="download" size="medium" solo />
      </Button>
    );
  },

  renderFullscreen() {
    if (!this.props.fullscreen) {
      return null;
    }

    return (
      <Button
        className="MediaPlayer-fullscreen"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.makeFullscreen}
        tooltip="Fullscreen"
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-fullscreen">
        <Glyph name="fullscreen" size="medium" solo />
      </Button>
    );
  },

  renderPlayback() {
    const glyphName = this.state.playing ? 'pause' : 'play';

    return (
      <Button
        className="MediaPlayer-playback"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.togglePlayback}
        eventElementName="MediaPlayer-playback">
        <Glyph name={glyphName} size="large" solo />
      </Button>
    );
  },

  renderPlaybackSlider() {
    return (
      <div
        className={cx({
          'MediaPlayer-playbackSlider': true,
          'MediaPlayer-playbackSliderSmall': this.isComponentSize(MediaPlayer.SMALL),
        })}>
        <Slider
          min={0}
          max={this.state.totalTime}
          step={0.1}
          value={this.state.currentTime}
          onBeforeChange={this.onBeforeChangePlayback}
          onChange={this.onChangePlayback}
          onAfterChange={this.onAfterChangePlayback}
          theme={this.props.theme}
          size="thick"
        />
      </div>
    );
  },

  renderTime() {
    const showHours = parseInt(this.state.totalTime / 3600, 10) > 0;

    if (this.isComponentSize(MediaPlayer.SMALL)) {
      return this.renderTimeSmall(showHours);
    }

    return this.renderTimeNormal(showHours);
  },

  renderTimeNormal(showHours) {
    return (
      <div className="MediaPlayer-time">
        <div>
          <div className="MediaPlayer-currentTime">
            {formatMediaTime(this.state.currentTime, showHours)}
          </div>
          <div className="MediaPlayer-timeDivider">/</div>
          <div className="MediaPlayer-totalTime">
            {formatMediaTime(this.state.totalTime, showHours)}
          </div>
        </div>
      </div>
    );
  },

  renderTimeSmall(showHours) {
    return (
      <div className="MediaPlayer-time MediaPlayer-timeSmall">
        <div className="MediaPlayer-currentTime">
          {formatMediaTime(this.state.currentTime, showHours)}
        </div>
      </div>
    );
  },

  renderVolume() {
    if (this.isComponentSize(MediaPlayer.SMALL)) {
      return null;
    }

    let tooltipMessage = 'Mute';
    let volume;
    if (this.state.muted || (this.state.volume === 0.0)) {
      tooltipMessage = 'Unmute';
      volume = 'mute';
    } else if (this.state.volume < 0.33) {
      volume = '1';
    } else if (this.state.volume < 0.67) {
      volume = '2';
    } else {
      volume = '3';
    }

    return (
      <Button
        className="MediaPlayer-volume"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.toggleMute}
        tooltip={tooltipMessage}
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-volume">
        {/* $FlowIssue */}
        <Glyph name={`audio-level-${volume}`} size="large" solo />
      </Button>
    );
  },

  renderVolumeSlider() {
    if (this.isComponentSize(MediaPlayer.SMALL)) {
      return null;
    }

    return (
      <div className="MediaPlayer-volumeSlider">
        <Slider
          min={0.0}
          max={1.0}
          step={0.1}
          value={this.state.muted ? 0.0 : this.state.volume}
          onChange={this.onChangeVolume}
          theme={this.props.theme}
          size="thin"
        />
      </div>
    );
  },

  render() {
    return (
      <div className={cx('MediaPlayer', getPropClassesFromInstance(this), this.props.className)}>
        {this.renderPlayback()}
        {this.renderPlaybackSlider()}
        {this.renderTime()}
        {this.renderVolume()}
        {this.renderVolumeSlider()}
        {this.renderDownload()}
        {this.renderFullscreen()}
      </div>
    );
  },
});

export default MediaPlayer;



// WEBPACK FOOTER //
// ./src/components/MediaPlayer.jsx